import { useState } from "react";
import PayoutApi from "../../../api/payoutApi";
import Input from "../../../components/Input/Input";
import BottomModal from "../../../components/Modals/BottomModal/BottomModal";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../../components/StandartButton/StandartButton";
import {
  BankPayRequest,
  PayoneerPayRequest,
  PayRequestsStatuses,
} from "../../../types/PayoutTypes";
import Notify from "../../../utils/toaster";
import s from "./Bank.module.css";
import BankRequestTable from "./BankTable/BankTable";

type BankProps = {
  requestData: BankPayRequest[];
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (page: number) => void;
  count: number;
  setRequests: React.Dispatch<
    React.SetStateAction<{
      bankRequests: BankPayRequest[];
      payoneerRequests: PayoneerPayRequest[];
    }>
  >;
};

const Bank = ({
  requestData,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  count,
  setRequests,
}: BankProps) => {
  const token = localStorage.getItem("token");
  const [selectedRequest, setSelectedRequest] = useState<BankPayRequest>();
  const [statusReasonModal, setStatusReasonModal] = useState<{
    modal: boolean;
    reason: string;
    status?: PayRequestsStatuses;
  }>({ modal: false, reason: "" });

  const statusesWithCancelationReason = [
    PayRequestsStatuses.FAILED,
    PayRequestsStatuses.REJECTED,
  ];

  const excludedStatuses = [
    PayRequestsStatuses.NEW,
    PayRequestsStatuses.CANCELED,
  ];

  const payRequestStatusOptions: { value: string; lable: string }[] =
    Object.values(PayRequestsStatuses)
      .filter((status) => !excludedStatuses.includes(status))
      .map((status) => ({
        value: status,
        lable: status.charAt(0).toUpperCase() + status.slice(1),
      }));

  const changeStatus = async (
    newStatus: PayRequestsStatuses,
    cancelationReason?: string
  ) => {
    if (!token || !selectedRequest) return;
    setSelectedRequest((prev) => ({
      ...prev!,
      status: newStatus,
      cancelationReason: cancelationReason,
    }));
    const payload = {
      requestId: selectedRequest._id,
      status: newStatus,
      cancelationReason: cancelationReason,
    };
    const response = await PayoutApi.changeBankPayRequestStatus(token, payload);
    if (response.status && response.request) {
      setRequests((prev) => ({
        ...prev,
        bankRequests: prev.bankRequests.map((item) => {
          if (item._id === selectedRequest._id) {
            return {
              ...item,
              status: newStatus,
              cancelationReason: cancelationReason,
            };
          }
          return item;
        }),
      }));
    }
    if (!response.status) {
      const oldRequestData = requestData.find(
        (el) => el._id === selectedRequest._id
      );
      setSelectedRequest(oldRequestData);
      if (response.message) Notify(response.message);
    }
  };

  return (
    <div>
      <BankRequestTable
        count={count}
        page={page}
        rows={requestData}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        setSelectedRequest={setSelectedRequest}
        setRequests={setRequests}
      />
      <BottomModal
        headTitle="Payout info"
        modalIsOpen={selectedRequest ? true : false}
        alternativeCloseFunction={() => setSelectedRequest(undefined)}
      >
        {selectedRequest && (
          <div className={s.card}>
            <MultipleSelect
              data={payRequestStatusOptions}
              multiplie={false}
              value={selectedRequest.status}
              setValue={(value) => {
                if (
                  statusesWithCancelationReason.includes(
                    value as PayRequestsStatuses
                  )
                ) {
                  setStatusReasonModal({
                    modal: true,
                    reason: "",
                    status: value as PayRequestsStatuses,
                  });
                  return;
                }
                changeStatus(value as PayRequestsStatuses);
              }}
              disableSortData
              lable="Change status"
              disabled={selectedRequest.status === PayRequestsStatuses.NEW}
            />
            <div className={s.row}>
              <span className={s.label}>Name:</span>
              <span>
                {selectedRequest.payoutInfo.name}{" "}
                {selectedRequest.payoutInfo.surname}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.label}>Bank:</span>
              <span>{selectedRequest.payoutInfo.bankName}</span>
            </div>
            <div className={s.row}>
              <span className={s.label}>IBAN / Account:</span>
              <span>{selectedRequest.payoutInfo.ibanOrAccNumber}</span>
            </div>
            {selectedRequest.payoutInfo.swiftOrBic && (
              <div className={s.row}>
                <span className={s.label}>SWIFT / BIC:</span>
                <span>{selectedRequest.payoutInfo.swiftOrBic}</span>
              </div>
            )}
            <div className={s.row}>
              <span className={s.label}>Address:</span>
              <span>{selectedRequest.payoutInfo.address}</span>
            </div>
            <div className={s.row}>
              <span className={s.label}>EGRPOU / INN:</span>
              <span>{selectedRequest.payoutInfo.egrpouOrInn}</span>
            </div>
            <div className={s.row}>
              <span className={s.label}>Currency:</span>
              <span>{selectedRequest.payoutInfo.currency.toUpperCase()}</span>
            </div>
            {statusesWithCancelationReason.includes(selectedRequest.status) &&
            selectedRequest.cancelationReason?.length ? (
              <div className={s.cancelReasonBlock}>
                <div>Cancelation Reason:</div>
                <div>{selectedRequest.cancelationReason}</div>
              </div>
            ) : null}
          </div>
        )}
      </BottomModal>
      <ModalWindow
        isOpen={statusReasonModal.modal}
        alternativeCloseFunction={() =>
          setStatusReasonModal({ modal: false, reason: "" })
        }
        width="fit-content"
      >
        <div className={s.reasonModal}>
          <span>
            Please provide a reason for rejecting your request (optional)
          </span>
          <Input
            inputValue={statusReasonModal.reason}
            onChangeInput={(value) =>
              setStatusReasonModal((prev) => ({ ...prev, reason: value }))
            }
            isVisible
            required
          />
          <div className={s.flexEnd}>
            <div
              role="button"
              className={s.closeBtn}
              onClick={() => setStatusReasonModal({ modal: false, reason: "" })}
            >
              Close
            </div>
            <StandartButton
              action={() => {
                changeStatus(
                  statusReasonModal.status!,
                  statusReasonModal.reason
                );
                setStatusReasonModal({ modal: false, reason: "" });
              }}
              buttonTitle="Accept"
            />
          </div>
        </div>
      </ModalWindow>
    </div>
  );
};

export default Bank;
